/**
 * Hierarchy Depth Tab for A/B Testing ResultsDashboard
 *
 * Sprint 11e Package 3: Displays agreement metrics at each taxonomy tier level.
 *
 * Shows how model agreement changes as taxonomy depth increases:
 * - Tier 1: Broad categories (e.g., "Technology")
 * - Tier 2: Sub-categories (e.g., "Software")
 * - Tier 3: Specific topics (e.g., "AI/ML")
 * - Tier 4: Narrow focus (e.g., "Machine Learning")
 * - Tier 5: Most specific (e.g., "Neural Networks")
 *
 * Expected pattern: Agreement decreases at deeper tiers as
 * models may classify the same email differently at granular levels.
 *
 * @see docs/sprints/ownyou-sprint11e-spec.md
 */

import { useMemo } from 'react'
import type { RichModelResults } from '@ownyou/ab-testing'
import { computeHierarchyMatrix } from '@ownyou/ab-testing'

interface HierarchyDepthTabProps {
  results: Map<string, RichModelResults>
}

const TIER_LABELS: Record<number, string> = {
  1: 'Tier 1 (Broad)',
  2: 'Tier 2 (Category)',
  3: 'Tier 3 (Topic)',
  4: 'Tier 4 (Specific)',
  5: 'Tier 5 (Granular)',
}

function formatModelName(modelKey: string): string {
  // Split on first colon only - model names may contain colons (e.g., Ollama "qwen3:8b")
  const [provider, ...modelParts] = modelKey.split(':')
  const model = modelParts.join(':')
  const shortModel = model?.split('/').pop() || model
  return `${provider?.charAt(0).toUpperCase()}${provider?.slice(1)} ${shortModel}`
}

function getShortModelName(modelKey: string): string {
  // Split on first colon only - model names may contain colons (e.g., Ollama "qwen3:8b")
  const [, ...modelParts] = modelKey.split(':')
  const model = modelParts.join(':')
  const shortModel = model?.split('/').pop() || model
  return shortModel?.substring(0, 12) || modelKey.substring(0, 12)
}

function getHeatmapColor(value: number): string {
  const intensity = Math.floor(value * 255)
  return `rgb(${255 - intensity}, 255, ${255 - intensity})`
}

function getAgreementColor(value: number): string {
  if (value >= 0.8) return 'text-green-600'
  if (value >= 0.6) return 'text-yellow-600'
  return 'text-red-600'
}

function getAgreementBgColor(value: number): string {
  if (value >= 0.8) return 'bg-green-500'
  if (value >= 0.6) return 'bg-yellow-500'
  return 'bg-red-500'
}

export function HierarchyDepthTab({ results }: HierarchyDepthTabProps) {
  const modelKeys = useMemo(() => Array.from(results.keys()), [results])

  // Check if we have rich classification data with tiers
  const hasRichData = useMemo(() => {
    for (const [, r] of results) {
      for (const c of r.classifications) {
        if (c.tier1 || c.tier2) return true
      }
    }
    return false
  }, [results])

  // Compute hierarchy agreement matrix
  const hierarchyMatrix = useMemo(() => {
    if (!hasRichData) return null
    return computeHierarchyMatrix(results)
  }, [results, hasRichData])

  if (!hasRichData) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Taxonomy Hierarchy Analysis</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-800 font-medium mb-2">
            Hierarchy Data Not Available
          </div>
          <p className="text-yellow-700 text-sm">
            Taxonomy hierarchy metrics require the full IAB classifier workflow (Sprint 11e).
            <br />
            The current results were generated using simple prompt classification.
          </p>
        </div>
      </div>
    )
  }

  if (!hierarchyMatrix) return null

  // Get valid tiers (those with data)
  const validTiers = [1, 2, 3, 4, 5].filter((tier) => {
    const agreement = hierarchyMatrix[tier]
    return (
      agreement &&
      (agreement.fullAgreementCount > 0 ||
        agreement.partialAgreementCount > 0 ||
        agreement.noAgreementCount > 0)
    )
  })

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Taxonomy Hierarchy Analysis</h3>

      {/* Tier agreement overview chart */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="font-medium text-gray-700 mb-4">Agreement by Tier Depth</h4>
        <div className="space-y-4">
          {validTiers.map((tier) => {
            const agreement = hierarchyMatrix[tier]
            const total =
              agreement.fullAgreementCount +
              agreement.partialAgreementCount +
              agreement.noAgreementCount

            return (
              <div key={tier} className="flex items-center gap-4">
                <div className="w-32 text-sm font-medium text-gray-600">
                  {TIER_LABELS[tier]}
                </div>
                <div className="flex-1">
                  <div className="h-6 bg-gray-200 rounded overflow-hidden flex">
                    {total > 0 && (
                      <>
                        <div
                          className="bg-green-500 h-full"
                          style={{
                            width: `${(agreement.fullAgreementCount / total) * 100}%`,
                          }}
                          title={`Full: ${agreement.fullAgreementCount}`}
                        />
                        <div
                          className="bg-yellow-500 h-full"
                          style={{
                            width: `${(agreement.partialAgreementCount / total) * 100}%`,
                          }}
                          title={`Partial: ${agreement.partialAgreementCount}`}
                        />
                        <div
                          className="bg-red-400 h-full"
                          style={{
                            width: `${(agreement.noAgreementCount / total) * 100}%`,
                          }}
                          title={`None: ${agreement.noAgreementCount}`}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="w-24 text-right">
                  <span className={`font-medium ${getAgreementColor(agreement.agreementRate)}`}>
                    {(agreement.agreementRate * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-6 mt-4 pt-4 border-t text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span>Full Agreement</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded" />
            <span>Partial Agreement</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-400 rounded" />
            <span>No Agreement</span>
          </div>
        </div>
      </div>

      {/* Agreement rate comparison table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h4 className="font-medium text-gray-700">Agreement Rate by Tier</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 text-left font-medium text-gray-700">Tier</th>
                <th className="p-3 text-right font-medium text-gray-700">Agreement</th>
                <th className="p-3 text-right font-medium text-gray-700">Full</th>
                <th className="p-3 text-right font-medium text-gray-700">Partial</th>
                <th className="p-3 text-right font-medium text-gray-700">None</th>
                <th className="p-3 text-right font-medium text-gray-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {validTiers.map((tier) => {
                const agreement = hierarchyMatrix[tier]
                const total =
                  agreement.fullAgreementCount +
                  agreement.partialAgreementCount +
                  agreement.noAgreementCount

                return (
                  <tr key={tier} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{TIER_LABELS[tier]}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded overflow-hidden">
                          <div
                            className={`h-full ${getAgreementBgColor(agreement.agreementRate)}`}
                            style={{ width: `${agreement.agreementRate * 100}%` }}
                          />
                        </div>
                        <span className={`font-medium ${getAgreementColor(agreement.agreementRate)}`}>
                          {(agreement.agreementRate * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-green-600 font-medium">
                      {agreement.fullAgreementCount}
                    </td>
                    <td className="p-3 text-right text-yellow-600 font-medium">
                      {agreement.partialAgreementCount}
                    </td>
                    <td className="p-3 text-right text-red-600 font-medium">
                      {agreement.noAgreementCount}
                    </td>
                    <td className="p-3 text-right text-gray-600">{total}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pairwise agreement per tier (expandable) */}
      {validTiers.map((tier) => {
        const agreement = hierarchyMatrix[tier]
        const pairwise = agreement.pairwiseAgreement

        // Skip if no pairwise data
        if (!pairwise || Object.keys(pairwise).length === 0) return null

        return (
          <details key={tier} className="border rounded-lg overflow-hidden">
            <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 flex items-center justify-between">
              <span className="font-medium text-gray-700">
                {TIER_LABELS[tier]} Pairwise Agreement
              </span>
              <span className={`text-sm ${getAgreementColor(agreement.agreementRate)}`}>
                {(agreement.agreementRate * 100).toFixed(0)}% overall
              </span>
            </summary>
            <div className="p-4 overflow-x-auto">
              <table className="min-w-full border">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 bg-gray-50 text-xs"></th>
                    {modelKeys.map((key) => (
                      <th
                        key={key}
                        className="border px-2 py-1 bg-gray-50 text-xs truncate max-w-24"
                        title={formatModelName(key)}
                      >
                        {getShortModelName(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modelKeys.map((rowKey) => (
                    <tr key={rowKey}>
                      <td
                        className="border px-2 py-1 bg-gray-50 text-xs font-medium truncate max-w-24"
                        title={formatModelName(rowKey)}
                      >
                        {getShortModelName(rowKey)}
                      </td>
                      {modelKeys.map((colKey) => {
                        const value = pairwise[rowKey]?.[colKey] || 0
                        return (
                          <td
                            key={colKey}
                            className="border px-2 py-1 text-center text-xs"
                            style={{ backgroundColor: getHeatmapColor(value) }}
                          >
                            {(value * 100).toFixed(0)}%
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )
      })}

      {/* Explanation */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-700 mb-2">Understanding Hierarchy Agreement</h4>
        <p className="text-sm text-gray-600">
          Agreement typically <strong>decreases</strong> at deeper tier levels. This is expected
          behavior - models may agree that an email is about "Technology" (Tier 1) but disagree
          on whether it's "Software Engineering" or "Data Science" (Tier 3).
        </p>
        <p className="text-sm text-gray-600 mt-2">
          <strong>High Tier 1 + Low Tier 5:</strong> Models agree on broad categories but differ on specifics.
          <br />
          <strong>Low Tier 1:</strong> Fundamental disagreement on topic classification.
        </p>
      </div>
    </div>
  )
}
